// Mail search: query-syntax parsing, JMAP Email/query filter building, and a
// client-side fallback filter for data already in memory.
//
// Supported operators (Gmail-style):
//   from:raka  to:budi  has:attachment|file  is:unread|read|encrypted|starred
//   label:Work  before:2026-11-01  after:2026-10-01   (dates are YYYY-MM-DD)
// Free words become full-text (JMAP `text`).

export const PAGE_SIZES = [5, 25, 50, 100];
export const TIME_RANGES = [
  ['any', 'Any time'], ['today', 'Today'], ['7', 'Last 7 days'], ['30', 'Last 30 days'], ['older', 'Older than 30 days'],
];
export const SORTS = [
  ['new', 'Newest first'], ['old', 'Oldest first'], ['unread', 'Unread first'], ['sender', 'Sender A–Z'],
];
export const emptyAdvanced = () => ({ time: 'any', label: '', from: '', sort: 'new' });

export function parseQuery(q) {
  const out = { text: [], from: [], to: [], has: [], is: [], label: [], before: null, after: null };
  for (const t of (q || '').trim().toLowerCase().split(/\s+/).filter(Boolean)) {
    const m = t.match(/^(from|to|has|is|label|before|after):(.+)$/);
    if (!m) out.text.push(t);
    else if (m[1] === 'before' || m[1] === 'after') out[m[1]] = m[2];
    else out[m[1]].push(m[2]);
  }
  return out;
}

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
function timeBounds(range, now = new Date()) {
  const today = dayStart(now);
  const ago = (n) => new Date(today.getTime() - n * 86400000);
  switch (range) {
    case 'today': return { after: today };
    case '7': return { after: ago(7) };
    case '30': return { after: ago(30) };
    case 'older': return { before: ago(30) };
    default: return {};
  }
}

/** Build a JMAP Email/query filter (RFC 8621 §4.4.1) from the query + advanced panel. */
export function toJmapFilter({ mailboxId, q, chip = 'all', adv = emptyAdvanced(), labelKeyword = (l) => `$km-label-${l.toLowerCase()}` }) {
  const p = parseQuery(q);
  const f = { inMailbox: mailboxId };
  if (p.text.length) f.text = p.text.join(' ');
  const from = [...p.from, ...(adv.from.trim() ? [adv.from.trim()] : [])];
  if (from.length) f.from = from.join(' ');
  if (p.to.length) f.to = p.to.join(' ');
  if (p.has.some((h) => /attach|file/.test(h)) || chip === 'attach') f.hasAttachment = true;
  if (p.is.includes('unread') || chip === 'unread') f.notKeyword = '$seen';
  if (p.is.includes('read')) f.hasKeyword = '$seen';
  const kws = [];
  if (p.is.includes('starred')) kws.push('$flagged');
  if (p.is.includes('encrypted') || chip === 'enc') kws.push('$kryptamail-pqc');
  for (const l of p.label) kws.push(labelKeyword(l));
  if (adv.label && adv.label !== 'none') kws.push(labelKeyword(adv.label));
  const tb = timeBounds(adv.time);
  if (p.after) tb.after = new Date(p.after);
  if (p.before) tb.before = new Date(p.before);
  if (tb.after) f.after = tb.after.toISOString();
  if (tb.before) f.before = tb.before.toISOString();
  // JMAP allows one hasKeyword per condition; AND them together.
  if (kws.length > 1) return { operator: 'AND', conditions: [f, ...kws.map((k) => ({ hasKeyword: k }))] };
  if (kws.length === 1) f.hasKeyword = f.hasKeyword || kws[0];
  return f;
}

export function toJmapSort(sort) {
  switch (sort) {
    case 'old': return [{ property: 'receivedAt', isAscending: true }];
    case 'unread': return [{ property: 'hasKeyword', keyword: '$seen', isAscending: true }, { property: 'receivedAt', isAscending: false }];
    case 'sender': return [{ property: 'from', isAscending: true }];
    default: return [{ property: 'receivedAt', isAscending: false }];
  }
}

/** Client-side equivalent for already-loaded messages (used for optimistic UI and tests). */
export function filterLocal(list, { q, chip = 'all', adv = emptyAdvanced(), now = new Date() }) {
  const p = parseQuery(q);
  const text = p.text.join(' ');
  const tb = timeBounds(adv.time, now);
  if (p.after) tb.after = new Date(p.after);
  if (p.before) tb.before = new Date(p.before);
  const fromQ = [...p.from, ...(adv.from.trim() ? [adv.from.trim().toLowerCase()] : [])];
  const out = list.filter((m) => {
    const hay = `${m.fromName || ''} ${m.fromEmail || ''} ${m.subject || ''} ${m.preview || ''}`.toLowerCase();
    if (text && !hay.includes(text)) return false;
    if (fromQ.length && !fromQ.every((f) => `${m.fromName || ''} ${m.fromEmail || ''}`.toLowerCase().includes(f))) return false;
    if ((chip === 'attach' || p.has.some((h) => /attach|file/.test(h))) && !m.hasAttachment) return false;
    if ((chip === 'unread' || p.is.includes('unread')) && m.read) return false;
    if ((chip === 'enc' || p.is.includes('encrypted')) && !m.encrypted) return false;
    if (p.is.includes('starred') && !m.starred) return false;
    const labels = m.labels || [];
    const want = [...p.label.map((l) => l.toLowerCase()), ...(adv.label && adv.label !== 'none' ? [adv.label.toLowerCase()] : [])];
    if (want.length && !want.every((l) => labels.map((x) => x.toLowerCase()).includes(l))) return false;
    if (adv.label === 'none' && labels.length) return false;
    const ts = new Date(m.receivedAt);
    if (tb.after && ts < tb.after) return false;
    if (tb.before && ts >= tb.before) return false;
    return true;
  });
  const byDate = (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt);
  if (adv.sort === 'old') out.sort((a, b) => -byDate(a, b));
  else if (adv.sort === 'unread') out.sort((a, b) => (a.read - b.read) || byDate(a, b));
  else if (adv.sort === 'sender') out.sort((a, b) => (a.fromName || a.fromEmail || '').localeCompare(b.fromName || b.fromEmail || ''));
  else out.sort(byDate);
  return out;
}

export function paginate(list, page, pageSize) {
  const pages = Math.max(1, Math.ceil(list.length / pageSize));
  const p = Math.min(page, pages - 1);
  return { rows: list.slice(p * pageSize, (p + 1) * pageSize), page: p, pages, total: list.length, from: list.length ? p * pageSize + 1 : 0, to: Math.min(list.length, (p + 1) * pageSize) };
}

export const activeFilterCount = (adv) => (adv.time !== 'any') + !!adv.label + !!adv.from.trim() + (adv.sort !== 'new');
